import { z } from 'zod';
import { MARKETS } from './markets.js';
import { SCOPE_FLAGS } from './scope-flags.js';
import { SUB_SEGMENTS } from './sub-segments.js';
import { OPPORTUNITY_BUCKETS, type OpportunityBucket } from './market-opportunity.js';

// API contracts shared between apps/api (server) and apps/web (client).
// Both sides parse with the same Zod schemas — when the wire shape changes,
// it changes in one place.

// --- /markets ---

export const marketSummarySchema = z.object({
  code: z.enum(MARKETS),
  name: z.string(),
  defaultScope: z.enum(SCOPE_FLAGS),
});

export const marketsResponseSchema = z.object({
  markets: z.array(marketSummarySchema),
});

export type MarketSummary = z.infer<typeof marketSummarySchema>;
export type MarketsResponse = z.infer<typeof marketsResponseSchema>;

// --- /entities ---

export const entitiesQuerySchema = z.object({
  country: z.enum(MARKETS).optional(),
  subSegment: z.enum(SUB_SEGMENTS).optional(),
  scopeFlag: z.enum(SCOPE_FLAGS).optional(),
  limit: z.number().int().positive().max(500).default(50),
  offset: z.number().int().nonnegative().default(0),
});

// Latest entity_financial_period (DISTINCT ON period_end_date DESC),
// inlined on the /entities response so the Screener can render financial
// columns without an N+1 follow-up call. Numeric values arrive as
// strings — Postgres numeric → text — and are parsed client-side.
// Null when the entity has no filed periods, or when the endpoint
// doesn't populate this (e.g. /entities/by-ids).
export const latestFinancialsSchema = z
  .object({
    revenue: z.string().nullable(),
    totalAssets: z.string().nullable(),
    equity: z.string().nullable(),
    creditors: z.string().nullable(),
    employeeCount: z.number().int().nullable(),
    /** Same value from the second-most-recent filed period — null when the
     *  entity has only one period on file. Used by the Screener to compute
     *  y/y growth client-side. */
    priorRevenue: z.string().nullable().optional(),
    priorEmployeeCount: z.number().int().nullable().optional(),
  })
  .nullable();

export const entitySummarySchema = z.object({
  id: z.string().uuid(),
  countryCode: z.enum(MARKETS),
  registryId: z.string(),
  legalName: z.string().nullable(),
  subSegment: z.enum(SUB_SEGMENTS).nullable(),
  primaryNace: z.string().nullable(),
  city: z.string().nullable(),
  status: z.string().nullable(),
  scopeFlag: z.enum(SCOPE_FLAGS),
  healthcareItConfidence: z.string().nullable(),
  /** ISO datetime of the entity's registration with the national
   *  registrar. Used to compute "years active" for the Screener filter
   *  + the Identity panel hero. Optional because legacy endpoints
   *  (/entities/by-ids) don't populate it. */
  registrationDate: z.string().nullable().optional(),
  /** Resolved roll-up group label (enm.economic_group) — search surfaces
   *  cluster member entities under their group header. Optional because
   *  legacy endpoints (/entities/by-ids) don't populate it. */
  economicGroup: z.string().nullable().optional(),
  latestFinancials: latestFinancialsSchema.optional(),
});

export const paginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export const entitiesListResponseSchema = z.object({
  data: z.array(entitySummarySchema),
  pagination: paginationSchema,
});

export type EntitySummary = z.infer<typeof entitySummarySchema>;
export type EntitiesListResponse = z.infer<typeof entitiesListResponseSchema>;

// --- /entities/:id ---

// Coerce nullable strings/dates with the loose shape the DB hands us. We keep
// the schema deliberately permissive on the client side; the panel renders
// gracefully when a field is null and the scope flag says "data not yet
// available".
export const entityDetailSchema = entitySummarySchema.extend({
  tradingNames: z.array(z.string()).nullable(),
  legalForm: z.string().nullable(),
  registrationDate: z.string().nullable(),
  dissolutionDate: z.string().nullable(),
  primaryNace: z.string().nullable(),
  secondaryNaces: z.array(z.string()).nullable(),
  region: z.string().nullable(),
  regionCode: z.string().nullable(),
  postcode: z.string().nullable(),
  addressLine: z.string().nullable(),
  lat: z.string().nullable(),
  lon: z.string().nullable(),
  parentEntityId: z.string().uuid().nullable(),
  sourceCompleteness: z.record(z.string(), z.boolean()).nullable(),
  firstSeenAt: z.string(),
  lastIngestedAt: z.string(),
});

export const entityDetailResponseSchema = z.object({
  data: entityDetailSchema,
});

export type EntityDetail = z.infer<typeof entityDetailSchema>;
export type EntityDetailResponse = z.infer<typeof entityDetailResponseSchema>;

// --- /entities/:id/financials ---

export const financialPeriodSchema = z.object({
  id: z.string().uuid(),
  periodStartDate: z.string(),
  periodEndDate: z.string(),
  revenue: z.string().nullable(),
  operatingProfit: z.string().nullable(),
  ebitda: z.string().nullable(),
  netProfit: z.string().nullable(),
  totalAssets: z.string().nullable(),
  equity: z.string().nullable(),
  debt: z.string().nullable(),
  employeeCount: z.number().int().nullable(),
  filingDate: z.string().nullable(),
  filingFormat: z.string().nullable(),
  filingCurrency: z.string().nullable(),
});

export const financialsResponseSchema = z.object({
  data: z.array(financialPeriodSchema),
});

export type FinancialPeriodSummary = z.infer<typeof financialPeriodSchema>;
export type FinancialsResponse = z.infer<typeof financialsResponseSchema>;

// --- /market/opportunity ---

export const marketOpportunityCellSchema = z.object({
  country: z.enum(MARKETS),
  region: z.string(),
  bucket: z.enum(
    OPPORTUNITY_BUCKETS.map((b) => b.key) as [OpportunityBucket, ...OpportunityBucket[]],
  ),
  nTotal: z.number(),
  nSurfaced: z.number(),
  marketRevenueEur: z.number(),
  addressableRevenueEur: z.number(),
  independentN: z.number(),
  independentEur: z.number(),
  /** 0..1 share of market revenue that is filed (not modelled). */
  reportedShare: z.number(),
  /** 0..1 top-5 revenue share. */
  /** null when the cell's top five are tied — concentration is not measurable. */
  cr5: z.number().nullable(),
  /** 0..1 Herfindahl–Hirschman index over economic groups (Σ shareᵢ²). */
  /** null when the cell's top five are tied — see `cr5`. */
  hhi: z.number().nullable(),
  /** 0..100 EUR-anchored index; null when nTotal < 5. */
  opportunityIndex: z.number().nullable(),
});

export const marketOpportunityResponseSchema = z.object({
  cells: z.array(marketOpportunityCellSchema),
  buckets: z.array(z.object({ key: z.string(), label: z.string() })),
  countries: z.array(z.string()),
  /** NUTS region level the region-heatmap cells are grouped at (default nuts2). */
  level: z.enum(['legacy', 'nuts1', 'nuts2', 'nuts3']).optional(),
});

export type MarketOpportunityCell = z.infer<typeof marketOpportunityCellSchema>;
export type MarketOpportunityResponse = z.infer<typeof marketOpportunityResponseSchema>;
